import React, { forwardRef, } from 'react';
import styled from 'styled-components';
import { Text } from '../Text';
import { Stack } from '../Stack';
import { Times, Check, AlertFilled } from '../../icons';
import { Addon } from './Addon';
const Component = styled.div `
  ${({ disabled, error, success, warning, theme, lineBottom }) => {
    if (!lineBottom)
        return;
    if (success)
        return `border-bottom: 2px solid ${theme.colors.success}`;
    if (warning)
        return `border-bottom: 2px solid ${theme.colors.warning}`;
    if (disabled || !error)
        return `border-bottom: 2px solid ${theme.colors.gray200}`;
    return `border-bottom: 2px solid ${theme.colors.error}`;
}};
  display: flex;
  position: relative;
  transition: border-bottom-color 0.2s ease-in-out;
  width: 100%;

  ${({ icon }) => icon && 'display: flex'};

  & input {
    background-color: ${({ theme }) => theme.colors.white};
    border: 0;
    color: ${({ disabled, theme }) => disabled ? theme.colors.gray300 : theme.colors.black};
    font-size: ${({ theme }) => theme.fontSizes.x2};
    line-height: ${({ theme }) => theme.lineHeights.x2};
    padding: 0;
    padding-bottom: ${({ theme }) => theme.space.x8};
    width: 100%;

    ${({ disabled }) => (disabled ? 'cursor: not-allowed' : '')};
  }

  ${({ disabled, error, theme }) => !disabled &&
    !error &&
    `
      &:hover,
      &:focus {
        border-bottom-color: ${theme.colors.black};
      }
    `};

  & input:not(:disabled):-moz-placeholder {
    color: ${({ theme }) => theme.colors.gray700};
    transition: color 0.2s ease-in-out;
  }

  & input:not(:disabled)::-moz-placeholder {
    color: ${({ theme }) => theme.colors.gray700};
    transition: color 0.2s ease-in-out;
  }

  & input:not(:disabled):-ms-input-placeholder {
    color: ${({ theme }) => theme.colors.gray700};
    transition: color 0.2s ease-in-out;
  }

  & input:not(:disabled)::-webkit-input-placeholder {
    color: ${({ theme }) => theme.colors.gray700};
    transition: color 0.2s ease-in-out;
  }

  & input:not(:disabled):hover:-moz-placeholder,
  & input:not(:disabled):focus:-moz-placeholder {
    color: ${({ theme }) => theme.colors.black};
  }

  & input:not(:disabled):hover::-moz-placeholder,
  & input:not(:disabled):focus::-moz-placeholder {
    color: ${({ theme }) => theme.colors.black};
  }

  & input:not(:disabled):hover:-ms-input-placeholder,
  & input:not(:disabled):focus:-ms-input-placeholder {
    color: ${({ theme }) => theme.colors.black};
  }

  & input:not(:disabled):hover::-webkit-input-placeholder,
  & input:not(:disabled):focus::-webkit-input-placeholder {
    color: ${({ theme }) => theme.colors.black};
  }

  & label {
    bottom: ${({ theme }) => theme.lineHeights.x1};
    left: ${({ icon }) => (icon ? '2.5rem' : '0')};
    opacity: 0;
    position: absolute;
    transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;

    ${({ disabled, theme }) => disabled &&
    `
        color: ${theme.colors.gray300};
        cursor: not-allowed;
      `};
  }

  & input:not(:placeholder-shown) + label {
    transform: translateY(-${({ theme }) => theme.lineHeights.x1});
    opacity: 1;
  }

  & input[type='password']::-ms-reveal,
  input[type='password']::-ms-clear {
    display: none;
  }

  & input[type='password']::-webkit-contacts-auto-fill-button,
  input[type='password']::-webkit-credentials-auto-fill-button {
    visibility: hidden;
    display: none !important;
    pointer-events: none;
    height: 0;
    width: 0;
    margin: 0;
  }

  & [role='warning'] {
    bottom: calc(-${({ theme }) => theme.lineHeights.x1} - 2px);
    left: 0;
    position: absolute;
  }

  & [role='alert'] {
    bottom: calc(-${({ theme }) => theme.lineHeights.x1} - 2px);
    left: 0;
    position: absolute;
  }

  & [role='success'] {
    bottom: calc(-${({ theme }) => theme.lineHeights.x1} - 2px);
    left: 0;
    position: absolute;
  }
`;
const IconContainer = styled.div `
  height: 1.5rem;
  margin-right: 0.5rem;
  opacity: 0.6;
  width: 1.5rem;
`;
const Error = ({ children, id }) => (React.createElement(Stack, { htmlAttrs: { role: 'alert' }, layout: "horizontal", margin: "x4" },
    React.createElement(Times, null),
    React.createElement(Text, { color: "error", id: `${id}_error`, isBold: true, textSize: "x1" }, children)));
const Success = ({ children, id }) => (React.createElement(Stack, { htmlAttrs: { role: 'success' }, layout: "horizontal", margin: "x4" },
    React.createElement(Check, null),
    React.createElement(Text, { color: "success", id: `${id}_error`, isBold: true, textSize: "x1" }, children)));
const Warning = ({ children, id }) => (React.createElement(Stack, { htmlAttrs: { role: 'warning' }, layout: "horizontal", margin: "x4" },
    React.createElement(AlertFilled, { color: "warning" }),
    React.createElement(Text, { color: "warning", id: `${id}_warning`, isBold: true, textSize: "x1" }, children)));
export const Input = forwardRef(({ addon, disabled, blocked, error, success, warning, lineBottom = true, htmlAttrs, icon, loading, type, label = '', ariaLabel, autoComplete, maxLength, ...rest }, ref) => {
    const generatedId = label.replace(/\W/g, '-');
    return (React.createElement(Component, { disabled: disabled, error: error, icon: icon, success: success, warning: warning, lineBottom: lineBottom },
        icon && React.createElement(IconContainer, null, icon),
        React.createElement("input", { ...htmlAttrs, ...rest, autoComplete: autoComplete, disabled: disabled || blocked, id: `${generatedId}-input`, placeholder: label, "aria-label": ariaLabel, "aria-describedby": `${generatedId}-input`, ref: ref, type: type, maxLength: maxLength }),
        React.createElement(Text, { as: "label", textSize: "x1", htmlAttrs: { htmlFor: `${generatedId}-input` }, "aria-hidden": true }, label),
        React.createElement(Addon, { disabled: disabled, loading: loading, addon: addon }),
        !disabled && warning && React.createElement(Warning, { id: generatedId }, warning),
        !disabled && error && React.createElement(Error, { id: generatedId }, error),
        !disabled && success && React.createElement(Success, { id: generatedId }, success)));
});
Input.displayName = 'Input';
//# sourceMappingURL=index.js.map